/**
 * Cookie Manager Module
 *
 * Manages session cookie operations using js-cookie library.
 * Handles secure cookie flags and session state tracking.
 */

import Cookies from 'js-cookie'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger('lib/cookieManager')

// ============================================================================
// Constants
// ============================================================================

const COOKIE_NAME = 'conex_session_active'
const DEFAULT_EXPIRES_DAYS = 8 / 24 // 8 hours

// ============================================================================
// Cookie Trust Boundary Documentation
// ============================================================================

/**
 * CRITICAL SECURITY BOUNDARY
 *
 * Cookie Semantics (conex_session_active):
 * - Means: "This browser had a valid auth flow recently"
 * - Used as: Performance optimization to skip MSAL token operations
 * - NOT a security boundary: Can be manipulated via browser DevTools
 * - NOT proof of identity: Frontend must validate token via MSAL
 *
 * Trust Model:
 * 1. Frontend uses cookie for fast "definitely logged out" check
 * 2. Frontend calls acquireTokenSilent() if cookie exists (actual security check)
 * 3. Backend MUST validate JWT on every protected API request
 * 4. Backend never relies on cookie for authentication
 *
 * Why both cookie and token validation:
 * - Cookie alone: Fast but easily spoofed
 * - Token alone: Secure but slow MSAL call on every route change
 * - Combined: Fast path for "logged out" + security via token validation
 *
 * Cookie Lifecycle:
 * 1. Set on successful login (via login flow, not this module)
 * 2. Checked by watchdog every 60 seconds
 * 3. Checked by SecurityVerifier on route navigation
 * 4. Deleted on logout (frontend sends logoutRedirect to MSAL)
 * 5. If missing: User is considered logged out (session terminated)
 *
 * Backend Session Validation:
 * - Backend maintains separate session records (database or cache)
 * - Backend validates JWT claims (exp, issuer, audience)
 * - Backend never uses frontend cookie for authorization
 * - Frontend cookie expiration ≠ backend session expiration
 */

// ============================================================================
// Cookie Operations
// ============================================================================

/**
 * Set the session active cookie
 *
 * Sets a secure cookie indicating the user has an active session.
 * This cookie is a performance optimization - it does NOT replace backend
 * session validation. Frontend uses it to avoid expensive MSAL calls
 * when checking if user is logged out.
 *
 * @param expiresDays - Number of days until cookie expires (default: 1)
 * @returns true if cookie was set successfully
 */
export function setCookieActive(expiresDays: number = DEFAULT_EXPIRES_DAYS): boolean {
  try {
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:'
    Cookies.set(COOKIE_NAME, 'true', {
      expires: expiresDays,
      secure: isSecure,  // Only enforce secure flag over HTTPS
      sameSite: 'Strict',  // Not sent on cross-site requests
    })
    return true
  } catch (error) {
    log.error('Failed to set session cookie:', error)
    return false
  }
}

/**
 * Check if session active cookie exists
 *
 * Performs a fast check to see if user appears to have an active session.
 * This is NOT a security check - it's just a performance optimization.
 *
 * @returns true if cookie exists
 */
export function getCookieActive(): boolean {
  try {
    const value = Cookies.get(COOKIE_NAME)
    return value === 'true'
  } catch (error) {
    log.error('Failed to read session cookie:', error)
    return false
  }
}

/**
 * Remove the session active cookie
 *
 * Deletes the session cookie when user logs out. This is part of the
 * session termination protocol.
 *
 * @returns true if cookie was removed successfully
 */
export function removeCookieActive(): boolean {
  try {
    Cookies.remove(COOKIE_NAME)
    return true
  } catch (error) {
    log.error('Failed to remove session cookie:', error)
    return false
  }
}

/**
 * Alias for getCookieActive, preserved for backward compatibility in tests/callers.
 */
export function isCookieActive(): boolean {
  return getCookieActive()
}

/**
 * Refresh session cookie by removing and setting it again.
 */
export function refreshCookieActive(expiresDays: number = DEFAULT_EXPIRES_DAYS): boolean {
  const removed = removeCookieActive()
  const set = setCookieActive(expiresDays)
  return removed && set
}

